import { lazy, Suspense, useEffect, useState } from 'react';
import { Features } from '@/components/Features';
import { Footer } from '@/components/Footer';
import { HomeHeader } from '@/components/HomeHeader';
import { Hero } from '@/components/Hero';
import { HowItWorks } from '@/components/HowItWorks';
import { useLocation } from 'react-router-dom';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { SummaryHome } from '@/components/SummaryHome';
import { PageSEO } from '@/components/SEO';
import { ArrowRight } from 'lucide-react';

// Lazy load below-fold sections to reduce initial bundle and improve LCP
const CTA = lazy(() => import('@/components/CTA').then((m) => ({ default: m.CTA })));
const Testimonials = lazy(() => import('@/components/Testimonials').then((m) => ({ default: m.Testimonials })));
const LatestLawyers = lazy(() => import('@/components/LatestLawyers').then((m) => ({ default: m.LatestLawyers })));
const MediaCoverage = lazy(() => import('@/components/MediaCoverage').then((m) => ({ default: m.MediaCoverage })));
const BackedBy = lazy(() => import('@/components/BackedBy').then((m) => ({ default: m.BackedBy })));
const FaqsSection = lazy(() => import('@/components/FaqsSection').then((m) => ({ default: m.FaqsSection })));

function Home() {
  const location = useLocation();
  const [user] = useState(() => {
    // Guard SSR: en el build (Node) no existe localStorage. Devolver {} no cambia el
    // markup (user solo decide el destino de un click) → hidratación segura.
    if (typeof window === 'undefined') return {};
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : {};
  });

  useEffect(() => {
    // Remover la clase dark del HTML cuando se está en Home
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    const section = location.hash.substring(1);
    if (section) {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  // El bloque below-fold (lazy + Suspense) se monta DESPUÉS de hidratar. Con renderToString
  // un Suspense que "suspende" en el server no hidrata bien (React error #419). Difiriéndolo,
  // el HTML del server no lleva ese Suspense y la hidratación queda limpia. Es contenido por
  // debajo del hero: no afecta el LCP.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-neutral-50 to-neutral-100">
      <PageSEO
        title="Encontrá Abogados Online en Argentina - Consultá Gratis"
        description="Publicá tu caso gratis y recibí propuestas de abogados verificados en minutos. Abogado de familia, laboral, penal, civil, sucesiones e inmobiliario. +1.000 abogados en Argentina."
        canonicalPath="/"
        keywords={['abogados argentina', 'abogado online', 'abogado', 'abogado de familia', 'abogado laboral', 'abogado penalista', 'abogado civil', 'abogado sucesiones', 'abogados online', 'consulta legal', 'encontrar abogado']}
      />
      <HomeHeader />
      {/* Banner LegalBot - ya disponible */}
      <div
        onClick={() => window.open('/legalbot', '_blank', 'noopener,noreferrer')}
        className="sticky top-16 z-30 w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white py-3 px-4 cursor-pointer hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-lg"
      >
        <div className="container mx-auto flex items-center justify-center gap-2 text-sm sm:text-base font-medium">
          <span className="bg-white/25 px-2 py-1 rounded-md text-xs sm:text-sm font-bold">¡YA DISPONIBLE!</span>
          <span>LegalBot, tu agente de IA para abogados y estudios</span>
          <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </div>
      <main className="w-full">
        <Hero user={user} />
        <HowItWorks />
        <Features />
        <SummaryHome />
        {hydrated && (
          <Suspense fallback={null}>
            <Testimonials />
            <LatestLawyers />
            <MediaCoverage />
            <BackedBy />
            <CTA user={user} />
            <FaqsSection />
          </Suspense>
        )}
        <WhatsAppButton />
      </main>
      <Footer />
    </div>
  );
}

export default Home;